import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Building2, Plus, Pencil, Trash2, ShieldCheck, LogOut } from "lucide-react";
import LegalDocsManager from "@/components/LegalDocsManager";

const PLANS = [
  { key: "fino_200", label: "Fino a 200 candidati" },
  { key: "fino_300", label: "Fino a 300 candidati" },
  { key: "fino_500", label: "Fino a 500 candidati" },
  { key: "oltre_500", label: "Oltre 500 candidati" },
];

const EMPTY_TENANT = { name: "", admin_email: "", admin_password: "", plan: "fino_200" };

function SuperAdminLogin({ onAuth }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [reg, setReg] = useState({ name: "", email: "", password: "" });
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.role !== "super_admin") {
        setError("Questo accesso è riservato al super-admin della piattaforma.");
        await api.post("/auth/logout").catch(() => {});
        return;
      }
      onAuth(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/superadmin/register", reg);
      onAuth(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-slate-800/80 border-slate-700 text-slate-100" data-testid="superadmin-auth-card">
        <CardContent className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-lg bg-sky-400 text-slate-900 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display font-bold text-lg">ITS Match Platform</h1>
              <p className="text-xs text-slate-400">Accesso super-admin</p>
            </div>
          </div>
          {mode === "login" ? (
            <form onSubmit={doLogin} className="space-y-4">
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input data-testid="superadmin-login-email" type="email" required className="bg-slate-900 border-slate-600 text-slate-100"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label className="text-slate-300">Password</Label>
                <Input data-testid="superadmin-login-password" type="password" required className="bg-slate-900 border-slate-600 text-slate-100"
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              {error && <p data-testid="superadmin-auth-error" className="text-sm text-red-400">{error}</p>}
              <Button data-testid="superadmin-login-submit" type="submit" disabled={busy} className="w-full bg-sky-500 hover:bg-sky-400 text-slate-900">
                {busy ? "Accesso..." : "Accedi"}
              </Button>
              <button type="button" data-testid="superadmin-show-register" className="text-xs text-slate-400 hover:text-slate-200 w-full text-center"
                onClick={() => { setMode("register"); setError(""); }}>
                Prima configurazione? Registra il super-admin
              </button>
            </form>
          ) : (
            <form onSubmit={doRegister} className="space-y-4">
              <p className="text-xs text-slate-400">La registrazione è disponibile solo se non esiste ancora alcun super-admin.</p>
              <div>
                <Label className="text-slate-300">Nome</Label>
                <Input data-testid="superadmin-register-name" required className="bg-slate-900 border-slate-600 text-slate-100"
                  value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input data-testid="superadmin-register-email" type="email" required className="bg-slate-900 border-slate-600 text-slate-100"
                  value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} />
              </div>
              <div>
                <Label className="text-slate-300">Password</Label>
                <Input data-testid="superadmin-register-password" type="password" required minLength={8} className="bg-slate-900 border-slate-600 text-slate-100"
                  value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} />
              </div>
              {error && <p data-testid="superadmin-register-error" className="text-sm text-red-400">{error}</p>}
              <Button data-testid="superadmin-register-submit" type="submit" disabled={busy} className="w-full bg-sky-500 hover:bg-sky-400 text-slate-900">
                {busy ? "Registrazione..." : "Registra super-admin"}
              </Button>
              <button type="button" data-testid="superadmin-show-login" className="text-xs text-slate-400 hover:text-slate-200 w-full text-center"
                onClick={() => { setMode("login"); setError(""); }}>
                Hai già un account? Accedi
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function SuperAdminPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_TENANT);
  const [editForm, setEditForm] = useState({ name: "", plan: "fino_200", active: true, admin_password: "" });
  const [deleting, setDeleting] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  const isSuper = user && user.role === "super_admin";

  const load = () => api.get("/superadmin/tenants").then(({ data }) => setTenants(data)).catch(() => {});
  useEffect(() => { if (isSuper) load(); }, [isSuper]);

  if (!isSuper) {
    if (user === false || (user && user.role !== "super_admin")) {
      return <SuperAdminLogin onAuth={(u) => setUser(u)} />;
    }
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center" data-testid="superadmin-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-700 border-t-sky-400" />
      </div>
    );
  }

  const createTenant = async () => {
    try {
      await api.post("/superadmin/tenants", form);
      toast.success("Tenant ITS creato con account admin");
      setOpen(false);
      setForm(EMPTY_TENANT);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openEdit = (t) => {
    setEditing(t);
    setEditForm({ name: t.name, plan: t.plan, active: t.active, admin_password: "" });
  };

  const saveEdit = async () => {
    try {
      const payload = { name: editForm.name, plan: editForm.plan, active: editForm.active };
      if (editForm.admin_password) payload.admin_password = editForm.admin_password;
      await api.put(`/superadmin/tenants/${editing.id}`, payload);
      toast.success("Tenant aggiornato");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteTenant = async () => {
    setDeleteBusy(true);
    try {
      await api.delete(`/superadmin/tenants/${deleting.id}`);
      toast.success(`Tenant "${deleting.name}" eliminato definitivamente`);
      setDeleting(null);
      setConfirmName("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100">
      <header className="h-16 bg-slate-900/95 border-b border-slate-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-sky-400 text-slate-900 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display font-bold">ITS Match Platform</div>
              <div className="text-xs text-slate-400">Console super-admin</div>
            </div>
          </div>
          <Button data-testid="superadmin-logout" variant="outline" size="sm" className="border-slate-600 text-slate-200 hover:bg-slate-700"
            onClick={async () => { await logout(); navigate("/superadmin"); }}>
            <LogOut className="h-4 w-4 mr-2" /> Esci
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="superadmin-dashboard">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-400">Multi-tenant</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold">Istituti ITS clienti</h1>
          </div>
          <Button data-testid="superadmin-add-tenant" className="bg-sky-500 hover:bg-sky-400 text-slate-900" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> Nuovo tenant
          </Button>
        </div>

        <Card className="bg-slate-800/80 border-slate-700 mt-8">
          <CardContent className="p-0">
            <Table data-testid="superadmin-tenants-table">
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Istituto</TableHead>
                  <TableHead className="text-slate-400">Email admin</TableHead>
                  <TableHead className="text-slate-400">URL candidati</TableHead>
                  <TableHead className="text-slate-400">Piano</TableHead>
                  <TableHead className="text-slate-400">Candidature</TableHead>
                  <TableHead className="text-slate-400">Stato</TableHead>
                  <TableHead className="text-slate-400"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((t) => (
                  <TableRow key={t.id} data-testid={`tenant-row-${t.id}`} className="border-slate-700 hover:bg-slate-700/50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-sky-400" />
                        <span className="font-medium text-slate-100">{t.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300 text-sm">{t.admin_email}</TableCell>
                    <TableCell>
                      {t.slug ? (
                        <a data-testid={`tenant-url-${t.id}`} href={`/candidati/${t.slug}`} target="_blank" rel="noreferrer"
                          className="text-sky-400 hover:underline text-xs font-mono2">
                          /candidati/{t.slug}
                        </a>
                      ) : <span className="text-slate-500 text-xs">—</span>}
                    </TableCell>
                    <TableCell><Badge className="bg-slate-700 text-slate-200">{t.plan_label}</Badge></TableCell>
                    <TableCell className="font-mono2 text-slate-200">{t.candidates_count}</TableCell>
                    <TableCell>
                      <Badge className={t.active ? "bg-green-900 text-green-300" : "bg-red-900 text-red-300"}>
                        {t.active ? "Attivo" : "Disattivato"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button data-testid={`tenant-edit-${t.id}`} variant="ghost" size="sm"
                          className="text-slate-300 hover:text-slate-100 hover:bg-slate-700" onClick={() => openEdit(t)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button data-testid={`tenant-delete-${t.id}`} variant="ghost" size="sm"
                          className="text-red-400 hover:text-red-200 hover:bg-red-950/50"
                          onClick={() => { setDeleting(t); setConfirmName(""); }}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {tenants.length === 0 && (
                  <TableRow className="border-slate-700"><TableCell colSpan={6} className="text-center text-slate-400 py-8">Nessun tenant registrato.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <LegalDocsManager />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="tenant-create-dialog">
          <DialogHeader><DialogTitle>Nuovo tenant ITS</DialogTitle><DialogDescription>Crea l'istituto cliente e il suo account amministratore.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome istituto</Label>
              <Input data-testid="tenant-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Email admin dell'istituto</Label>
              <Input data-testid="tenant-form-admin-email" type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} />
            </div>
            <div>
              <Label>Password temporanea admin</Label>
              <Input data-testid="tenant-form-admin-password" type="text" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} />
              <p className="text-xs text-slate-500 mt-1">Comunicala all'istituto: servirà per il primo accesso.</p>
            </div>
            <div>
              <Label>Piano abbonamento (predisposizione)</Label>
              <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                <SelectTrigger data-testid="tenant-form-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLANS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">La fatturazione Stripe sarà integrata in seguito; il piano è solo informativo.</p>
            </div>
            <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
              Alla creazione, l'admin dell'istituto riceverà automaticamente un'email con il link personalizzato
              della pagina candidati e le credenziali di primo accesso.
            </p>
            <Button data-testid="tenant-form-save" className="w-full bg-[#003366] hover:bg-[#002244]"
              disabled={!form.name.trim() || !form.admin_email.trim() || form.admin_password.length < 8} onClick={createTenant}>
              Crea tenant
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editing)} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="tenant-edit-dialog">
          <DialogHeader><DialogTitle>Modifica tenant</DialogTitle><DialogDescription>Aggiorna nome, piano, stato o password admin dell'istituto.</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Nome istituto</Label>
                <Input data-testid="tenant-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div>
                <Label>Piano</Label>
                <Select value={editForm.plan} onValueChange={(v) => setEditForm({ ...editForm, plan: v })}>
                  <SelectTrigger data-testid="tenant-edit-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLANS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid="tenant-edit-active" checked={editForm.active} onCheckedChange={(v) => setEditForm({ ...editForm, active: v })} />
                <Label>Tenant attivo</Label>
              </div>
              <div>
                <Label>Reset password admin (lascia vuoto per non cambiare)</Label>
                <Input data-testid="tenant-edit-admin-password" type="text" value={editForm.admin_password}
                  onChange={(e) => setEditForm({ ...editForm, admin_password: e.target.value })} />
              </div>
              <Button data-testid="tenant-edit-save" className="w-full bg-[#003366] hover:bg-[#002244]" onClick={saveEdit}>
                Salva modifiche
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleting)} onOpenChange={(v) => !v && setDeleting(null)}>
        <DialogContent data-testid="tenant-delete-dialog">
          <DialogHeader>
            <DialogTitle>Elimina tenant</DialogTitle>
            <DialogDescription>Operazione irreversibile: tutti i dati dell'istituto saranno rimossi dal database.</DialogDescription>
          </DialogHeader>
          {deleting && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Stai per eliminare definitivamente <strong>{deleting.name}</strong> e tutti i dati associati:
                corsi, banca domande, <strong>{deleting.candidates_count} candidature</strong>, sessioni di test
                (log e snapshot) e account di accesso (admin e candidati).
              </p>
              <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
                I file caricati su Google Drive non verranno rimossi.
              </p>
              <div>
                <Label>Digita il nome dell'istituto per confermare</Label>
                <Input data-testid="tenant-delete-confirm-name" value={confirmName} placeholder={deleting.name}
                  onChange={(e) => setConfirmName(e.target.value)} />
              </div>
              <Button data-testid="tenant-delete-confirm-button" className="w-full bg-red-600 hover:bg-red-500 text-white"
                disabled={confirmName.trim() !== deleting.name || deleteBusy} onClick={deleteTenant}>
                {deleteBusy ? "Eliminazione in corso..." : "Elimina definitivamente"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
